import { FC, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { Divider } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import IconButton from 'components/_core/IconButton';
import Text from 'components/_core/Text';
import { PPatp } from 'helpers/date';

import {
  getDeviceLastActivityFormattedDate,
  getDeviceLocation,
} from '../../../../helpers';
import ConfirmButton from '../ConfirmButton';
import locales from './locales';
import { BlockAuthenticationDeviceDocument } from './mutations/blockAuthenticationDevice.gql';
import * as S from './styles';
import { Props } from './types';

const ConfirmBlockDevice: FC<Props> = ({ close, device, next, previous }) => {
  const lastActivityDate = getDeviceLastActivityFormattedDate(device, PPatp);

  const [blockAuthenticationDevice, { loading }] = useMutation(
    BlockAuthenticationDeviceDocument,
  );

  const blockDevice = useCallback(async () => {
    await blockAuthenticationDevice({
      variables: {
        input: {
          authenticationDeviceId: device.authenticationDeviceId,
          phone: device.phone,
        },
      },
    });
    next();
  }, [blockAuthenticationDevice, next, device]);

  const location = getDeviceLocation(device);

  return (
    <>
      <S.Header>
        <IconButton
          aria-label=""
          icon="cross"
          onClick={close}
          variant="secondary"
        />
      </S.Header>

      <S.Body>
        <S.DetailsHeader>
          <S.Title>{locales.blockThisDeviceTitle}</S.Title>
          <Text>{locales.blockThisDeviceSubtitle}</Text>
        </S.DetailsHeader>

        <S.Properties>
          <S.Property>
            <S.PropertyLabel>{locales.device}</S.PropertyLabel>
            <S.PropertyValue>
              {device.model || locales.unknownDeviceType}
            </S.PropertyValue>
          </S.Property>

          {location ? (
            <>
              <S.Property>
                <Divider />
              </S.Property>

              <S.Property>
                <S.PropertyLabel>{locales.location}</S.PropertyLabel>
                <S.PropertyValue>{location}</S.PropertyValue>
              </S.Property>
            </>
          ) : null}

          {lastActivityDate ? (
            <>
              <S.Property>
                <Divider />
              </S.Property>

              <S.Property>
                <S.PropertyLabel>{locales.lastConnection}</S.PropertyLabel>
                <S.PropertyValue>{lastActivityDate}</S.PropertyValue>
              </S.Property>
            </>
          ) : null}
        </S.Properties>
      </S.Body>

      <S.Footer>
        <ConfirmButton
          isLoading={loading}
          onClick={blockDevice}
          variant="danger"
        >
          {locales.blockThisDevice}
        </ConfirmButton>
        <Button
          isLoading={loading}
          onClick={previous}
          variant="inline-secondary"
        >
          {locales.cancel}
        </Button>
      </S.Footer>
    </>
  );
};

export default ConfirmBlockDevice;
